import type { EvidencePack } from '@cyberscore/contracts';
import { useQuery } from '@tanstack/react-query';
import { api, apiBlob } from '../../lib/api';
import { useSession, can } from '../../lib/session';

/** The RMiT evidence pack (task 50): the insurer's administrators and auditors. */
export function useEvidenceAccess() {
  const { tenant } = useSession();
  return tenant?.kind !== 'platform' && can(tenant, 'evidence_pack.view');
}

export interface Period {
  from: string;
  to: string;
}

const query = (p: Partial<Period>) => {
  const q = new URLSearchParams(Object.entries(p).filter(([, v]) => v) as [string, string][]).toString();
  return q ? `?${q}` : '';
};

/** Building a pack is recorded in the audit trail, so it is built when asked for, not on every visit. */
export function useEvidencePack(period: Partial<Period> | null) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: ['evidence-pack', tenant?.id, period],
    queryFn: () => api<EvidencePack>('GET', `/evidence-pack${query(period ?? {})}`),
    enabled: !!period,
    staleTime: Infinity,
    retry: false,
  });
}

/** Saves through the browser's download. */
export function save(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function savePack(pack: EvidencePack) {
  const name = `rmit-evidence-${pack.tenant.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${pack.period.from}-to-${pack.period.to}.json`; // prettier-ignore
  save(new Blob([JSON.stringify(pack, null, 2)], { type: 'application/json' }), name);
}

export async function saveAuditTrail(period: Period) {
  save(await apiBlob(`/evidence-pack/audit-trail.csv${query(period)}`), `audit-trail-${period.from}-to-${period.to}.csv`); // prettier-ignore
}
